// auth code that's not directly relevant for redux store

import { Configuration, InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import _ from "lodash";
import { DISPLAY_VERSION, getDefaultAppName, getSessionId, LOGGED_OUT_URI, MVISION_AUTHORITY } from "../environments";
import { AppVersionInfo } from "../store/app-version-info";
import { isAuthError } from "../util/errors";
import { QUERY_PARAM_SESSION_ID } from "./backend-api-interface";
import { BackendFetchOptions } from "./cloud-backend-auth";

export const POPUPS_BLOCKED_ERROR = 'POPUPS_BLOCKED_ERROR';
export const POPUP_WINDOW_ERROR = 'popup_window_error';

/** Models authentication to an azure app registration. Do not put into redux store! */
export default class AppAuth {

  /** Name of the app registration */
  appName: string;

  /** Client ID matching the azure app registration */
  clientId: string;

  /** MSAL configuration object */
  config: Configuration;

  /** The set of scopes that we ask for when requesting tokens */
  request: any;

  /** MSAL instance object */
  msalInstance: PublicClientApplication | null;

  /** True if user has logged into this app registration, false otherwise */
  isLoggedIn: boolean;

  /** Name of the user who has logged in with this app auth. */
  loggedInUsername: string | undefined;

  /** The version of the app that this app auth is sending requests for. */
  appVersion: AppVersionInfo | undefined;

  constructor(appName: string, clientId: string) {
    this.appName = appName;
    this.clientId = clientId;

    this.isLoggedIn = false;

    this.request = {
      scopes: [`${clientId}/.default`],
    };

    this.config = {
      auth: {
        clientId: this.clientId,
        authority: MVISION_AUTHORITY,
      },
      cache: {
        cacheLocation: "sessionStorage",
      },
      // system: {
      //     // uncomment this if needed
      //     loggerOptions: {
      //         loggerCallback: (logLevel: LogLevel, message: string, containsPii: boolean) => console.log(`${this.appName}: ${message}`),
      //         logLevel: LogLevel.Info,
      //         // change this to true if needed -- don't commit or deploy into production
      //         piiLoggingEnabled: false,
      //     },
      // },

    };

    this.msalInstance = null;
  }

  setAppVersion(appVersion: AppVersionInfo) {
    this.appVersion = appVersion;
  }

  /** Attempts to asynchronously log into an azure app registration. */
  async logIn() {
    if (this.msalInstance === null) { this.msalInstance = new PublicClientApplication(this.config); }

    if (!this.isLoggedIn) {
      // No user signed in
      try {
        const authResult = await this.msalInstance.loginPopup();
        const { account } = authResult;
        if (account === null) {
          throw new Error('No valid account after logon');
        }
        this.msalInstance.setActiveAccount(account);
        this.isLoggedIn = true;
        this.loggedInUsername = account.name || account.username;
      }
      catch (err) {
        console.error(err);
        if (isAuthError(err) && 'errorCode' in err && err.errorCode.includes('popup_window_error')) {
          throw new Error(POPUPS_BLOCKED_ERROR);
        } else if (isAuthError(err) && 'errorCode' in err && (err.errorCode.includes('block_nested_popups'))) {
          // ignore block_nested_popups errors to prevent visible error dialogs showing up on
          // login popup windows
        }
        else throw err;
      }
    }
  }

  async logOut() {
    if (this.msalInstance && this.isLoggedIn) {
      // No user signed in
      try {
        await this.msalInstance.logoutPopup({
          // postLogoutRedirectUri: "./loggedOut.html",
          mainWindowRedirectUri: LOGGED_OUT_URI,
        });
      }
      catch (err) {
        console.error(err);
        if (isAuthError(err) && 'errorCode' in err && (err.errorCode.includes('popup_window_error') || err.errorCode.includes('user_cancelled'))) {
          throw new Error(POPUPS_BLOCKED_ERROR);
        }
        else throw err;
      }
    }

    this.isLoggedIn = false;
  }

  private async getAccessToken(): Promise<string> {
    if (this.msalInstance === null) {
      throw new Error(`No MSAL instance for ${this.appName} -- log in before trying to get an access token!`);
    }

    const authInstance = this.msalInstance as PublicClientApplication;

    // throw if an active account has not been set
    let account = authInstance.getActiveAccount();
    if (!account) {
      throw new Error('No active account has been set');
    }

    let response: any = {};
    const request = { ...this.request };

    // try to get the token silently, but fall back to popup window if it fails
    //
    // NOTE/TODO: asynchronous parallel operations (such as sending dicoms for auto-contouring, if this app was rtviewer)
    // may cause problems if the current access token (inside authInstance) has timed out and
    // msal has to fall back to acquireTokenPopup as this may cause parallel popup
    // windows which in turn will cause them to cancel each other. If this turns out to
    // be an actual problem then this code must be changed so that in case of a stale
    // token the first token/auth call/api operation must be blocking.
    //
    // see also: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/token-lifetimes.md
    try {
      response = await authInstance.acquireTokenSilent(request);
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        response = await authInstance.acquireTokenPopup(request);
      }
    }

    return response.accessToken as string;
  }

  async fetch(url: string, httpRequestOptions: any = undefined, backendFetchOptions: Partial<BackendFetchOptions> = {}): Promise<Response> {
    if (!this.isLoggedIn) {
      throw new Error(`Must be logged into ${this.appName} before calling any MSAL APIs!`);
    }

    const token = await this.getAccessToken();

    const fetchOptions = httpRequestOptions || {};
    const bearer = `Bearer ${token}`;
    _.set(fetchOptions, 'headers.Authorization', bearer);

    if (!backendFetchOptions.allowCache) {
      _.set(fetchOptions, 'headers.Cache-Control', 'no-store');
      _.set(fetchOptions, 'cache', 'no-store');
      _.set(fetchOptions, 'headers.pragma', 'no-cache');
    }

    // set content type as JSON if it's not already set & we're POSTing JSON
    if (backendFetchOptions.postJson && !_.has(fetchOptions, 'headers.Content-Type')) {
      _.set(fetchOptions, 'headers.Content-Type', 'application/json');
    }

    // set app version so backend knows which app and which version of it is sending the request
    _.set(fetchOptions, 'headers.appVersion', `${getDefaultAppName()}/${DISPLAY_VERSION}/${this.appVersion?.commit || 'N/A'}`);

    const fullUrl = new URL(url);

    if (!backendFetchOptions.noClientId) {
      // append client ID to query parameters
      const clientIdQueryParam = `${QUERY_PARAM_SESSION_ID}=${getSessionId()}`;
      fullUrl.search = fullUrl.search ? `${fullUrl.search}&${clientIdQueryParam}` : clientIdQueryParam;
    }

    try {
      return await fetch(fullUrl.toString(), fetchOptions);
    } catch (err) {
      console.log(`An error occurred when trying to fetch from ${url}`);
      console.log('Fetch options:');
      console.log(fetchOptions);
      console.log('Fetch error:');
      console.log(err);
      throw err;
    }
  }
}
